import * as R from 'ramda';

import {
  CanCpa,
  DesignationsForm,
  ExtraInfo,
  JobDetails,
  NameFields,
  OnboardingForm,
  RegionMap,
  STEP_IDS,
  StepConfig,
  StepId,
  UsCpa,
} from './onboarding.types';

export const stepsConfig: Record<StepId, StepConfig> = {
  [STEP_IDS.introduction]: {
    id: STEP_IDS.introduction,
    initialInput: null,
    handleNext: (form, input: boolean) => ({
      ...form,
      isTeamUse: input,
    }),
    isStepInputValid: input => input !== null,
  },
  [STEP_IDS.isDesignated]: {
    id: STEP_IDS.isDesignated,
    initialInput: null,
    handleNext: (form, input) => ({
      ...form,
      userCountry: input ? null : form.userCountry,
      designations: input ? {} : null,
    }),
    isStepInputValid: input => typeof input === 'boolean',
  },
  [STEP_IDS.nonDesignatedRegion]: {
    id: STEP_IDS.nonDesignatedRegion,
    initialInput: null,
    isRequired: form => !form.designations,
    handleNext: (form, input) => ({
      ...form,
      userCountry: input as string,
    }),
    isStepInputValid: input => typeof input === 'string',
  },
  [STEP_IDS.designationTypes]: {
    id: STEP_IDS.designationTypes,
    initialInput: {},
    isRequired: form => !!form.designations,
    handleNext: (form, input) => ({
      ...form,
      designations: input as Record<string, Record<string, boolean>>,
    }),
    isStepInputValid: (input: DesignationsForm) =>
      Object.values(input).some(
        countryDesignations => Object.keys(countryDesignations).length > 0,
      ),
  },
  [STEP_IDS.disclaimer]: {
    id: STEP_IDS.disclaimer,
    initialInput: null,
    isRequired: (form, options) =>
      (options?.disclaimerDesignationOptions || []).filter(
        disclaimer =>
          !!form.designations?.[disclaimer.countryCode]?.[
            disclaimer.designation
          ],
      )?.length > 0,
    handleNext: form => form,
    isStepInputValid: () => true,
  },
  [STEP_IDS.canCpaProvinces]: {
    id: STEP_IDS.canCpaProvinces,
    initialInput: {},
    isRequired: form => !!form.designations?.CAN?.CPA,
    handleNext: (form, input) => ({
      ...form,
      designations: {
        ...form.designations,
        CAN: {
          ...form.designations?.CAN,
          CPA: {
            ...(form.designations?.CAN?.CPA as CanCpa),
            provinces: input as RegionMap,
          },
        },
      },
    }),
    isStepInputValid: input => Object.keys(input).length > 0,
  },
  [STEP_IDS.canCpaYear]: {
    id: STEP_IDS.canCpaYear,
    initialInput: null,
    isRequired: form => !!form.designations?.CAN?.CPA,
    handleNext: (form, input) => ({
      ...form,
      designations: {
        ...form.designations,
        CAN: {
          ...form.designations?.CAN,
          CPA: {
            ...(form.designations?.CAN?.CPA as CanCpa),
            year: input as number,
          },
        },
      },
    }),
    isStepInputValid: input => typeof input === 'number',
  },
  [STEP_IDS.usaCpaStates]: {
    id: STEP_IDS.usaCpaStates,
    initialInput: {},
    isRequired: form => !!form.designations?.USA?.CPA,
    handleNext: (form, input) => ({
      ...form,
      designations: {
        ...form.designations,
        USA: {
          ...form.designations?.USA,
          CPA: {
            ...(form.designations?.USA?.CPA as UsCpa),
            states: input as RegionMap,
          },
        },
      },
    }),
    isStepInputValid: input => Object.keys(input).length > 0,
  },
  [STEP_IDS.usaEaNumber]: {
    id: STEP_IDS.usaEaNumber,
    initialInput: null,
    isRequired: form => !!form.designations?.USA?.EA,
    handleNext: (form, input) => ({
      ...form,
      designations: {
        ...form.designations,
        USA: {
          ...form.designations?.USA,
          EA: input as string,
        },
      },
    }),
    isSubmitAllowed: input => typeof input === 'string',
    isStepInputValid: input =>
      typeof input === 'string' && PTIN_REGEX_PATTERN.test(input),
  },
  [STEP_IDS.bmuCpaYear]: {
    id: STEP_IDS.bmuCpaYear,
    initialInput: null,
    isRequired: form => !!form.designations?.BMU?.CPA,
    handleNext: (form, input) => ({
      ...form,
      designations: {
        ...form.designations,
        BMU: {
          ...form.designations?.BMU,
          CPA: {
            ...(form.designations?.BMU?.CPA as CanCpa),
            year: input as number,
          },
        },
      },
    }),
    isStepInputValid: input => typeof input === 'number',
  },
  [STEP_IDS.name]: {
    id: STEP_IDS.name,
    initialInput: {
      first: '',
      last: '',
    },
    handleNext: (form, input) => ({
      ...form,
      name: R.map(R.trim, input as NameFields),
    }),
    isStepInputValid: input =>
      Object.values(input).every(
        value => typeof value === 'string' && value.trim().length > 0,
      ),
  },
  [STEP_IDS.b2cJobDetails]: {
    id: STEP_IDS.b2cJobDetails,
    initialInput: {
      jobTitle: '',
      companyName: '',
    },
    isRequired: (_form, options) => !!options?.isB2CUser,
    handleNext: (form, input) => ({
      ...form,
      jobDetails: R.map(R.trim, input as JobDetails),
    }),
    isStepInputValid: (input: JobDetails) =>
      Object.values(input).every(
        value => typeof value === 'string' && value.trim().length > 0,
      ) && isSafeCompanyName(input.companyName),
  },
} as const;

const PTIN_REGEX_PATTERN = /^P\d{8}$/;

const isSafeCompanyName = (companyName: string) =>
  !/(?:\b[a-z][a-z\d+.-]*:\/\/|\bwww\.|\b[a-z\d-]+(?:\.[a-z\d-]+)+\/(?:\S*)?)/i.test(
    companyName,
  ) && !/<\/?[a-z][^>]*>/i.test(companyName);

export const getSteps = (
  form: OnboardingForm,
  stateParams: ExtraInfo = {
    isB2CUser: false,
    disclaimerDesignationOptions: [],
  },
) => {
  const steps = Object.keys(stepsConfig) as (keyof typeof STEP_IDS)[];
  return steps.filter(
    step => stepsConfig[step].isRequired?.(form, stateParams) ?? true,
  );
};

export const getPrevStep = ({
  steps,
  activeStep,
}: {
  steps: (keyof typeof STEP_IDS)[];
  activeStep: string;
}) => {
  const activeStepIndex = steps.findIndex(step => step === activeStep);
  return steps[activeStepIndex - 1] || activeStep;
};

export const getNextStep = ({
  steps,
  activeStep,
}: {
  steps: (keyof typeof STEP_IDS)[];
  activeStep: string;
}) => {
  const activeStepIndex = steps.findIndex(step => step === activeStep);
  return steps[activeStepIndex + 1] || activeStep;
};
